import React, { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { DEFAULT_THEME, getThemeById } from '../features/websites/templates';
import SEO from '../shared/seo/SEO';
import '../styles/viewer.css';
import GalleryPage from './GalleryPage';
import WebsiteSectionsCanvas from '../features/websites/rendering/WebsiteSectionsCanvas';
import { cx, useRuntimeCss } from '../shared/styles/runtimeCss';

const buildViewerRootStyles = (theme) => ({
  backgroundColor: theme?.colors?.background || DEFAULT_THEME.colors.background,
  color: theme?.colors?.text || DEFAULT_THEME.colors.text,
  fontFamily: theme?.fonts?.body || DEFAULT_THEME.fonts.body,
});

const buildFooterStyles = (theme) => ({
  backgroundColor: theme?.colors?.secondary || DEFAULT_THEME.colors.secondary,
  color: theme?.colors?.text || DEFAULT_THEME.colors.text,
  padding: '2rem 0',
  marginTop: '4rem',
});

const buildFooterLinkStyles = (theme) => ({
  color: theme?.colors?.primary || DEFAULT_THEME.colors.primary,
  fontWeight: 500,
});

const buildCenteredTextStyles = () => ({ textAlign: 'center' });
const buildFooterSpacingStyles = () => ({ marginTop: '1rem' });

function useMobileViewport() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
}

function useWebsiteViewerData(websiteId) {
  const [website, setWebsite] = useState(null);
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadWebsite() {
      try {
        const websiteRef = doc(db, 'websites', websiteId);
        const websiteDoc = await getDoc(websiteRef);
        if (!websiteDoc.exists()) {
          if (isMounted) setError('Уебсайтът не е намерен');
          return;
        }

        const websiteData = websiteDoc.data();
        if (!isMounted) return;
        setWebsite(websiteData);
        setTheme(getThemeById(websiteData.theme) || DEFAULT_THEME);
      } catch (loadError) {
        console.error('Error loading website:', loadError);
        if (isMounted) setError('Неуспешно зареждане на уебсайта');
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadWebsite();
    return () => {
      isMounted = false;
    };
  }, [websiteId]);

  return { website, theme, loading, error, setWebsite, setError };
}

function appendWeddingGalleryPhoto(website, section, imageUrl) {
  return website.sections.map((currentSection) => {
    if (currentSection.id !== section.id || currentSection.type !== 'weddingPhotoGallery') {
      return currentSection;
    }

    const currentUploadedPhotos = Array.isArray(currentSection.props.uploadedPhotos)
      ? currentSection.props.uploadedPhotos
      : [];

    return {
      ...currentSection,
      props: {
        ...currentSection.props,
        uploadedPhotos: [...currentUploadedPhotos, imageUrl],
      },
    };
  });
}

function useWeddingGalleryUpload({ website, websiteId, setWebsite, setError }) {
  return async (section, imageUrl) => {
    try {
      if (!website) return;

      const updatedSections = appendWeddingGalleryPhoto(website, section, imageUrl);
      await updateDoc(doc(db, 'websites', websiteId), { sections: updatedSections });
      setWebsite((previousWebsite) => ({ ...previousWebsite, sections: updatedSections }));
    } catch (updateError) {
      console.error('Error updating photos:', updateError);
      setError('Неуспешно обновяване на снимките. Моля, опитайте отново.');
    }
  };
}

function useViewerClassNames(theme) {
  const css = useRuntimeCss();

  return {
    viewerRootClass: useMemo(() => css(buildViewerRootStyles(theme)), [css, theme]),
    footerClass: useMemo(() => css(buildFooterStyles(theme)), [css, theme]),
    footerLinkClass: useMemo(() => css(buildFooterLinkStyles(theme)), [css, theme]),
    centeredTextClass: useMemo(() => css(buildCenteredTextStyles()), [css]),
    footerSpacingClass: useMemo(() => css(buildFooterSpacingStyles()), [css]),
  };
}

function WebsiteViewerLoading() {
  return (
    <div className="website-viewer-loading">
      <div className="spinner" />
      <p>Зареждане на уебсайта...</p>
    </div>
  );
}

function WebsiteViewerError({ error }) {
  return (
    <div className="website-viewer-error">
      <h2>Упс! Нещо се обърка</h2>
      <p>{error}</p>
      <button onClick={() => window.location.reload()} className="button primary">
        Опитай отново
      </button>
    </div>
  );
}

function ViewerHomeRoute({ website, theme, isMobile, onWeddingGalleryPhotoUpload, classNames }) {
  const heroSection = website.sections?.find(s => s.type === 'hero');
  const coupleNames = heroSection?.props?.title || website.title || 'Сватбен уебсайт';
  const weddingInfo = heroSection?.props?.subtitle || '';
  const heroImage = heroSection?.props?.image || '';
  const siteUrl = `https://www.nashatasvatba.in/site/${website.id}`;

  return (
    <div className={cx('website-viewer', classNames.viewerRootClass)}>
      <SEO
        title={coupleNames}
        description={`Сватбен уебсайт на ${coupleNames}. ${weddingInfo}. Вижте галерията, таймлайнът и RSVP-нете онлайн.`}
        ogTitle={`${coupleNames} - Сватба`}
        ogDescription={`Присъединете се към нас ${weddingInfo ? `на ${weddingInfo}` : ''} за сватбата на ${coupleNames}.`}
        ogImage={heroImage}
        ogUrl={siteUrl}
        canonical={siteUrl}
      />
      <WebsiteSectionsCanvas
        sections={website.sections}
        theme={theme}
        mode="viewer"
        isMobile={isMobile}
        customCss={website.customCSS}
        canvasClassName="website-content"
        onWeddingGalleryPhotoUpload={onWeddingGalleryPhotoUpload}
      />

      <div className={cx('website-footer', classNames.footerClass)}>
        <div className={cx('container', classNames.centeredTextClass)}>
          <p>© {new Date().getFullYear()} Всички права запазени</p>
          <p className={classNames.footerSpacingClass}>
            Създадено с{' '}
            <a
              href="https://www.nashatasvatba.in/"
              target="_blank"
              rel="noopener noreferrer"
              title="Сватбен уебсайт - Нашата Сватба"
              className={cx('website-renderer__footer-link', classNames.footerLinkClass)}
            >
              Нашата Сватба
            </a>
          </p>
        </div>
      </div>

      {website.customJS ? <script dangerouslySetInnerHTML={{ __html: website.customJS }} /> : null}
    </div>
  );
}

function WebsiteViewer() {
  const { websiteId } = useParams();
  const isMobile = useMobileViewport();
  const { website, theme, loading, error, setWebsite, setError } = useWebsiteViewerData(websiteId);
  const onWeddingGalleryPhotoUpload = useWeddingGalleryUpload({
    website,
    websiteId,
    setWebsite,
    setError,
  });
  const classNames = useViewerClassNames(theme);

  if (loading) return <WebsiteViewerLoading />;
  if (error) return <WebsiteViewerError error={error} />;
  if (!website) return null;

  return (
    <Routes>
      <Route
        path="/"
        element={
          <ViewerHomeRoute
            website={website}
            theme={theme}
            isMobile={isMobile}
            onWeddingGalleryPhotoUpload={onWeddingGalleryPhotoUpload}
            classNames={classNames}
          />
        }
      />
      <Route path="/gallery/:sectionId" element={<GalleryPage />} />
    </Routes>
  );
}

export default WebsiteViewer;
